import type moment from 'moment';
import { isString, omitBy, isNil, isObject } from 'lodash-es';
import type { CoreTypes } from '../../core';

export interface DivisionScope {
  id: string;
  start?: moment.MomentInput;
  end?: moment.MomentInput;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isObject(value);
}

function isDivisionScope(value: unknown): value is DivisionScope {
  return isRecord(value) && isString(value.id);
}

function getDivisionFromDomain(entity: Record<string, unknown>): DivisionScope | undefined {
  if (!Array.isArray(entity.domain)) return undefined;

  for (const domainReference of entity.domain) {
    if (!isRecord(domainReference) || domainReference.toModel !== 'divisions') continue;
    if (isDivisionScope(domainReference.to)) return domainReference.to;
    if (isString(domainReference.to)) return { id: domainReference.to };
  }

  return undefined;
}

export function getDivision(
  entity: unknown,
  domain?: DivisionScope
): DivisionScope | undefined {
  if (isDivisionScope(domain)) return domain;
  if (!isRecord(entity)) return undefined;

  return getDivisionFromDomain(entity);
}

export function hasDivisionId(
  entity: unknown,
  domain?: DivisionScope
): boolean {
  return isString(getDivisionId(entity, domain, false));
}

export function toReference<T extends Partial<{
  id:          string,
  domain?:     unknown,
  ids:         string,
  displayName: string
}>> (
  input:      T[],
  domain?: DivisionScope,
  expandReferenceNames?: boolean
) {
  return input.map((x) => {
    if (!hasDivisionId(x, domain))
      throw new Error('(SS12000::To::Reference) Some top layer entities are missing division values', { cause: 'invalid_input' });

    return {
      id: `${ getDivisionId(x, domain) }.${ isString(x) ? x : x.id }`,
      ...!isString(x) && {
        ...omitBy({
          foreignId: x.ids,
          ...expandReferenceNames && { displayName: x.displayName }
        }, isNil),
      }
    };
  });
}

export function getDivisionId(
  entity: unknown,
  domain?: DivisionScope,
  throwOnMissing = true
) {
  const resolvedDivision = getDivision(entity, domain);
  if (resolvedDivision?.id) return resolvedDivision.id;

  if (!throwOnMissing) return undefined;
  throw new Error('(SS12000::To::getDivisionId) entity is missing division id value', { cause: 'invalid_input' });
}